import { Tag, TagColor, Tooltip, useToggle } from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import { type ExpensableSpendingAlert } from 'modules/expenses/components/expenses/DataTable/types'
import { amountWithCurrency } from 'modules/finance/lib/budgets'

interface Props {
  expensableAmount: number
  alerts: ExpensableSpendingAlert[]
  isDuplicatedExpenseEnabled?: boolean
  locale: string
}

const isSpendingPolicyAlert = (
  alert: ExpensableSpendingAlert
): alert is ExpensableSpendingAlert & {
  source: { __typename: 'FinanceSpendingPolicyRuleGroup' }
} => {
  return alert.source?.__typename === 'FinanceSpendingPolicyRuleGroup'
}

function spendingPolicyTooltipMessage(
  alert: ExpensableSpendingAlert,
  expensableAmount: number,
  locale: string
): string {
  if (alert.source?.__typename === 'FinanceSpendingPolicy') {
    return alert.reason ?? ''
  }

  const category =
    isSpendingPolicyAlert(alert) &&
    alert.source?.spendingPolicyRulesConnection.edges.find(
      (rule) => rule.node.identifier === 'category'
    )?.node.formatedValue
  const currency =
    isSpendingPolicyAlert(alert) &&
    alert.source?.spendingPolicyRulesConnection.edges.find(
      (rule) => rule.node.identifier === 'currency'
    )?.node.value[0]
  const amount =
    isSpendingPolicyAlert(alert) &&
    alert.source?.spendingPolicyRulesConnection.edges.find(
      (rule) => rule.node.identifier === 'amount'
    )?.node.value[0]

  if (category && currency && amount) {
    return i18n.t('expenses_v2.expense.spending_alert.tooltip.spending_policy_with_details', {
      category,
      amount: amountWithCurrency(expensableAmount - parseInt(amount ?? '0'), currency, locale),
    })
  }

  return i18n.t('expenses_v2.expense.spending_alert.tooltip.spending_policy')
}

function policyComplianceLabel(alert: ExpensableSpendingAlert): string {
  return (
    alert.reason?.split(':')?.[0]?.trim() ??
    i18n.t('expenses_v2.expense.spending_alert.label.spending_policy')
  )
}

export const SpendingAlertPill = ({ expensableAmount, alerts, locale }: Props) => {
  const [open, toggle] = useToggle(false)

  const failingAlerts = alerts.filter((alert) => alert.status === 'failed')

  if (failingAlerts.length === 0) {
    return null
  }

  // we always use warning color, out of policy alert is not blocking
  const color = TagColor.tangerine

  const uniqueAlertsByType = failingAlerts.reduce<ExpensableSpendingAlert[]>((acc, alert) => {
    //we want to show all the alerts from the recommendation
    if (!acc.some((a) => a.type === alert.type && a.type !== 'policy_compliance')) {
      acc.push(alert)
    }
    return acc
  }, [])

  const tooltip = uniqueAlertsByType
    .map((alert) => {
      if (alert.type === 'policy_compliance') {
        return alert.reason ?? ''
      }
      if (alert.type === 'spending_policy') {
        return spendingPolicyTooltipMessage(alert, expensableAmount, locale)
      }
      if (alert.type === 'mileage_rate_changed') {
        return i18n.t('expenses_v2.expense.spending_alert.tooltip.mileage_rate_changed')
      }
      if (alert.type === 'duplicate_expense') {
        return i18n.t('expenses_v2.expense.spending_alert.tooltip.duplicate_expense')
      }
      if (alert.type === 'category_conflict') {
        return i18n.t('expenses_v2.expense.spending_alert.tooltip.category_conflict')
      }
      return ''
    })
    .filter(Boolean)
    .join('. ')

  // Default label for multiple alerts
  let label = `${uniqueAlertsByType.length} ${i18n.t(
    'expenses_v2.expense.spending_alert.label.multiple'
  )}`
  // Single alert case - keep existing label logic
  if (uniqueAlertsByType.length === 1 && uniqueAlertsByType[0]) {
    switch (uniqueAlertsByType[0].type) {
      case 'policy_compliance':
        label = policyComplianceLabel(uniqueAlertsByType[0])
        break
      case 'spending_policy':
        label = i18n.t('expenses_v2.expense.spending_alert.label.spending_policy')
        break
      case 'mileage_rate_changed':
        label = i18n.t('expenses_v2.expense.spending_alert.label.mileage_rate_changed')
        break
      case 'duplicate_expense':
        label = i18n.t('expenses_v2.expense.spending_alert.label.duplicate_expense')
        break
      case 'category_conflict':
        label = i18n.t('expenses_v2.expense.spending_alert.label.category_conflict')
        break
      default:
        break
    }
  }

  return (
    <Tooltip.Alt
      attachment="top center"
      label={tooltip}
      textAlign="center"
      open={open}
      toggle={toggle}
    >
      <Tag icon="alert-circle" leftItem="icon" size="normal" inline type={color}>
        {label}
      </Tag>
    </Tooltip.Alt>
  )
}
